var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Popover, Tooltip as MuiTooltip } from '@mui/material';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import useDashTheme from './useDashTheme';
import useResponsiveSidebar from './useResponsiveSidebar';
import useAccountIdentity from './useAccountIdentity';
import { getUserProfileData, logoutUser } from '@/utils';
import { requestLeave } from '@/lib/unsaved-changes';
import { requestTourStart } from '@/lib/product-tour';
import { isAtsEmbedActive } from '@/hoc/api/ats-embed';
// Lets the page inside the shell name its own crumb once its data loads
// (a job title, a candidate name) instead of the generic pageTitle.
const PageCrumbContext = createContext(() => undefined);
export function usePageCrumb(label) {
    const setLabel = useContext(PageCrumbContext);
    useEffect(() => {
        setLabel(label || null);
        return () => setLabel(null);
    }, [label, setLabel]);
}
/**
 * Standalone-page wrapper that reuses the dashboard's Sidebar + TopBar so
 * detail pages (JD detail, interview report, schedule interview) share the
 * exact same chrome as /dashboard instead of a separate header.
 */
export default function DashboardShell({ pageTitle, activeView = 'jobs', parents = [{ label: 'Dashboard', to: '/dashboard' }], children, }) {
    const [crumbLabel, setCrumbLabel] = useState(null);
    const navigate = useNavigate();
    const [dashTheme, toggleDashTheme] = useDashTheme();
    const themeClass = `vdash${dashTheme === 'light' ? ' vdash-light' : ''}`;
    const [sidebarOpen, setSidebarOpen] = useResponsiveSidebar();
    const [avatarAnchorEl, setAvatarAnchorEl] = useState(null);
    // Detail pages are reached from inside the embed too, so they hide the same
    // chrome — otherwise opening a JD from Job Postings would reveal the sidebar.
    const embedded = isAtsEmbedActive();
    const userData = getUserProfileData();
    const identity = useAccountIdentity();
    const fullName = identity.name;
    const handleAvatarClick = (event) => setAvatarAnchorEl(event.currentTarget);
    const handleAvatarClose = () => setAvatarAnchorEl(null);
    const handleMyProfile = () => {
        var _a;
        handleAvatarClose();
        const repId = (_a = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _a === void 0 ? void 0 : _a.representative_id;
        navigate(`/representative-profile${repId ? `?id=${repId}` : ''}`);
    };
    const handleOrgProfile = () => {
        handleAvatarClose();
        navigate('/organization-profile');
    };
    // VA-27514 — Account Settings must be reachable from every page's profile
    // menu, not just /dashboard. Same target as the dashboard's own menu item.
    const handleAccountSettings = () => {
        handleAvatarClose();
        navigate('/my-settings');
    };
    // VA-29262: the tour runs on the dashboard overview.
    const handleProductTour = () => {
        handleAvatarClose();
        requestTourStart();
        navigate('/dashboard');
    };
    const handleLogout = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        handleAvatarClose();
        // VA-29256: a page with unsaved work (the job wizard) gets to confirm first.
        if (!(yield requestLeave('logout')))
            return;
        logoutUser();
        if (window.Auth) {
            window.dispatchEvent(new Event('authChange'));
            (_b = (_a = window.Auth).logout) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
        window.location.href = '/';
    });
    const handleNavigate = (path) => {
        if (path === '/dashboard')
            navigate('/dashboard');
        else if (path === '/interviews')
            navigate('/dashboard?view=interviews');
        else if (path === '/jobs')
            navigate('/dashboard?view=jobs');
        else if (path === '/team')
            navigate('/dashboard?view=team');
        else if (path === '/my-settings')
            navigate('/dashboard?view=settings');
        else if (path === '/card-settings')
            navigate('/dashboard?view=card-settings');
        else
            navigate(path);
    };
    const profileMenu = (_jsx(Popover, { open: Boolean(avatarAnchorEl), anchorEl: avatarAnchorEl, onClose: handleAvatarClose, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, PaperProps: {
            className: themeClass,
            sx: {
                mt: 1,
                minWidth: 208,
                borderRadius: '10px',
                overflow: 'hidden',
                bgcolor: 'transparent',
                boxShadow: 'var(--dash-shadow-pop)',
            },
        }, children: _jsxs("div", { className: 'bg-dash-surface border border-dash-border rounded-[10px] overflow-hidden', children: [_jsx(MuiTooltip, { title: fullName, arrow: true, placement: 'left', children: _jsxs("div", { className: 'px-4 py-3 border-b border-dash-border flex items-center gap-3', children: [identity.avatar ? (_jsx("img", { src: identity.avatar, alt: fullName, className: 'w-9 h-9 rounded-full shrink-0 object-cover ring-1 ring-dash-borderStrong' })) : (_jsx("div", { className: 'w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold bg-dash-accentSoft text-dash-accentInk ring-1 ring-dash-borderStrong', children: fullName.charAt(0).toUpperCase() })), _jsxs("div", { className: 'min-w-0', children: [_jsx("p", { className: 'text-sm font-semibold text-dash-text truncate', children: fullName.charAt(0).toUpperCase() + fullName.slice(1) }), _jsx("p", { className: 'text-xs text-dash-muted truncate', children: identity.role || 'Member' })] })] }) }), _jsxs("div", { className: 'py-1', children: [_jsx("button", { onClick: handleMyProfile, className: 'w-full text-left px-4 py-2 text-[13px] font-medium text-dash-text2 hover:bg-dash-hover hover:text-dash-text transition-colors cursor-pointer', children: "My Profile" }), _jsx("button", { onClick: handleOrgProfile, className: 'w-full text-left px-4 py-2 text-[13px] font-medium text-dash-text2 hover:bg-dash-hover hover:text-dash-text transition-colors cursor-pointer', children: "Organization Profile" }), _jsx("button", { onClick: handleAccountSettings, className: 'w-full text-left px-4 py-2 text-[13px] font-medium text-dash-text2 hover:bg-dash-hover hover:text-dash-text transition-colors cursor-pointer', children: "Account Settings" }), _jsx("button", { onClick: handleProductTour, className: 'w-full text-left px-4 py-2 text-[13px] font-medium text-dash-text2 hover:bg-dash-hover hover:text-dash-text transition-colors cursor-pointer', children: "Take a product tour" }), _jsx("div", { className: 'border-t border-dash-border mt-1 pt-1', children: _jsx("button", { onClick: handleLogout, className: 'w-full text-left px-4 py-2 text-[13px] font-medium text-dash-dangerInk hover:bg-dash-dangerSoft transition-colors cursor-pointer', children: "Logout" }) })] })] }) }));
    return (_jsxs("div", { className: `${themeClass} flex h-full w-full overflow-hidden ${embedded ? 'bg-dash-bg' : 'bg-dash-bg2'} text-dash-text`, children: [profileMenu, !embedded && (_jsx(Sidebar, { isOpen: sidebarOpen, onToggle: () => setSidebarOpen((o) => !o), activeView: activeView, interviewBadge: 0, jobPostingsBadge: 0, userName: fullName, userRole: identity.role, userAvatar: identity.avatar, onNavigate: handleNavigate, onProfileClick: handleAvatarClick })), _jsxs("div", { className: `flex-1 flex flex-col overflow-hidden min-w-0 bg-dash-bg ${embedded ? '' : 'dash-shell-panel'}`, children: [_jsx(TopBar, { pageTitle: pageTitle, breadcrumbs: [...parents, { label: crumbLabel || pageTitle }], pageDate: moment().format('dddd, MMMM D, YYYY'), userName: fullName, userAvatar: identity.avatar, onAvatarClick: handleAvatarClick, onMenuToggle: embedded ? undefined : () => setSidebarOpen((o) => !o), embedded: embedded, theme: dashTheme, onToggleTheme: toggleDashTheme }), _jsx("div", { className: 'flex-1 min-h-0 overflow-y-auto', children: _jsx(PageCrumbContext.Provider, { value: setCrumbLabel, children: children }) })] })] }));
}
/**
 * True when this app is running standalone (its own tab/window on the origin
 * from REACT_APP_BASE_URL or a local dev host) — false when embedded via an
 * iframe by another application, where the host app owns the page chrome.
 */
export function isSelfHostedApp() {
    if (typeof window === 'undefined')
        return false;
    try {
        if (window.self !== window.top)
            return false;
    }
    catch (_a) {
        return false; // cross-origin frame access throws → we are embedded
    }
    const base = process.env.REACT_APP_BASE_URL;
    if (!base)
        return true;
    try {
        const { hostname } = window.location;
        return (window.location.origin === new URL(base).origin ||
            hostname === 'localhost' ||
            hostname === '127.0.0.1');
    }
    catch (_b) {
        return true;
    }
}
